import dayjs from "dayjs";
import { Field, Form, Formik, useFormikContext } from "formik";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";

import ButtonLoader from "@/Components/ButtonLoader";
import FieldErrorMessage from "@/Components/common/FieldErrorMessage";
import ModalPopUp from "@/Components/common/ModalPopUp";
import { RequiredSymbol } from "@/Components/common/RequiredSymbol";
import { RejectIcon } from "@/Components/Icons/RejectIcon";
import CustomTimePicker from "@/Components/ui/CustomTimePicker";
import { toastMessages } from "@/Constants/toastConstants";
import { _signOffJob } from "@/services/timesheet";
import { showWarningToast } from "@/utilities/utils";

const SignOffForm = ({ handleClose }) => {
  const {
    values,
    setValues,
    setFieldValue,
    errors,
    submitForm,
    isSubmitting,
    validateField,
    setFieldTouched,
  } = useFormikContext();

  useEffect(() => {
    if (values.endTime) {
      let startTimeObj = dayjs(values.startTime, "HH:mm").add(30, "minute");
      let endTimeObj = dayjs(values.endTime, "HH:mm");
      if (startTimeObj.isAfter(endTimeObj)) {
        setFieldValue("endTime", "");
        showWarningToast(toastMessages.endTimeNot30MinutesAfterStartTime, {
          id: toastMessages.endTimeNot30MinutesAfterStartTime
            .toLowerCase()
            .replace(/\s+/g, "-"),
        });
      }
    }
  }, [values.startTime, values.endTime]);
  return (
    <Form>
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col justify-start gap-3">
            <label
              htmlFor="startTime"
              className="text-[14px] font-medium leading-[normal] text-txtColor"
            >
              Start Time <RequiredSymbol />
            </label>
            <div className="custom_time_picker_wrapper relative">
              <CustomTimePicker
                value={
                  values.startTime ? dayjs(values.startTime, "HH:mm") : null
                }
                onChange={(value) => {
                  let startTime = value.format("HH:mm");
                  setFieldValue("startTime", startTime).then(() => {
                    setFieldTouched("startTime", true, true);
                  });
                }}
              />
              <FieldErrorMessage name="startTime" className={"text-red"} />
            </div>
          </div>
          <div className="flex flex-col justify-start gap-3">
            <label
              htmlFor="endTime"
              className="text-[14px] font-medium leading-[normal] text-txtColor"
            >
              End Time <RequiredSymbol />
            </label>
            <div className="custom_time_picker_wrapper relative">
              <CustomTimePicker
                value={values.endTime ? dayjs(values.endTime, "HH:mm") : null}
                onChange={(value) => {
                  let endTime = value.format("HH:mm");
                  setFieldValue("endTime", endTime).then(() => {
                    setFieldTouched("endTime", true, true);
                  });
                }}
              />
              <FieldErrorMessage name="endTime" className={"text-red"} />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          <div className="grid gap-3">
            <label
              htmlFor="breakTime"
              className="text-[14px] font-medium leading-[normal] text-txtColor"
            >
              Break Time
            </label>
            <div className="custom_time_picker_wrapper relative">
              <CustomTimePicker
                value={
                  values.breakTime ? dayjs(values.breakTime, "HH:mm") : null
                }
                onChange={(value) => {
                  let breakTime = value.format("HH:mm");
                  setFieldValue("breakTime", breakTime);
                }}
              />
              <FieldErrorMessage name="breakTime" className={"text-red"} />
            </div>
          </div>
          <div className="flex flex-col justify-start gap-3">
            <label
              htmlFor="adminTime"
              className="text-[14px] font-medium leading-[normal] text-txtColor"
            >
              Admin Time
            </label>
            <div className="custom_time_picker_wrapper relative">
              <CustomTimePicker
                value={
                  values.adminTime ? dayjs(values.adminTime, "HH:mm") : null
                }
                onChange={(value) => {
                  let adminTime = value.format("HH:mm");
                  setFieldValue("adminTime", adminTime);
                }}
              />
              {/* <FieldErrorMessage
                          name="adminTime"
                          className={"text-red"}
                        /> */}
            </div>
          </div>
          <div className="flex flex-col justify-start gap-3">
            <label
              htmlFor="noOfVisit"
              className="text-[14px] font-medium leading-[normal] text-txtColor"
            >
              No. Of Visit
            </label>
            <div className="flex flex-col">
              <Field
                type="text"
                className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 !outline-none !ring-0 focus:border-primary ${
                  errors.noOfVisit && touched.noOfVisit ? "!border-red-500" : ""
                }`}
                autoComplete="off"
                name="noOfVisit"
                placeholder="Enter No of Visits"
                onKeyUp={(e) => {
                  if (e.keyCode === 13) {
                    submitForm();
                  }
                }}
                validate={(value) => {
                  //
                }}
              />
              <FieldErrorMessage name="noOfVisit" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-[20px]">
          <button
            type="button"
            className="transition-linear relative h-[45px] w-[120px] rounded-lg border border-primary bg-primary text-[14px] font-medium text-white hover:bg-none hover:text-primary"
            onClick={submitForm}
          >
            {isSubmitting ? <ButtonLoader /> : <span>Confirm</span>}
          </button>
          <button
            type="button"
            className="transition-linear h-[45px] w-[120px] rounded-lg border border-primary bg-none text-[14px] font-medium text-primary hover:bg-primary hover:text-white"
            onClick={() => {
              handleClose();
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </Form>
  );
};

/**
 * Adjust Amendment Modal component.
 */
const SignOffModal = ({
  callbackFunc,
  toggleSignOffModal,
  updateSelectedTimesheets,
  showSignOffModal,
  selectedTimesheets,
  formData,
}) => {
  const dispatch = useDispatch();
  const {
    startTime = "",
    endTime = "",
    breakTime = "",
    adminTime = "",
    noOfVisit = "",
  } = formData || {};

  /**
   * A function to handle the closing of something.
   */
  const handleClose = () => {
    dispatch(toggleSignOffModal(false));
  };

  useEffect(() => {
    if (!showSignOffModal) {
      dispatch(updateSelectedTimesheets([]));
    }
  }, [showSignOffModal]);

  return (
    <ModalPopUp
      show={showSignOffModal}
      panelClass="max-w-[800px]"
      onClose={handleClose}
      panelContainerClass="flex items-center justify-center"
    >
      <div className="flex flex-col gap-4">
        <div className="flex justify-between">
          <h3 className="text-[20px] font-semibold leading-[normal] text-black">
            Sign off Timesheet
          </h3>
          <button
            onClick={() => {
              handleClose();
            }}
            className="text-red outline-none ring-0"
          >
            <RejectIcon />
          </button>
        </div>
        <div>
          <Formik
            initialValues={{
              startTime: startTime,
              endTime: endTime,
              breakTime: breakTime,
              adminTime: adminTime,
              noOfVisit: noOfVisit,
            }}
            enableReinitialize={false}
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            onSubmit={(values, { setSubmitting }) => {
              let data = {
                start_time: values.startTime,
                end_time: values.endTime,
                break_time: values.breakTime,
                admin_time: values.adminTime,
                no_of_visit: values.noOfVisit,
                timesheet_id: selectedTimesheets[0].timesheet_id, // to be added
              };
              let response = _signOffJob(data);
              response
                .then((res) => {
                  console.log("_saveAdjustAmendment", res);
                  const status = res?.status;
                  if (status === 200) {
                    const msg =
                      res?.data?.message || "Timesheet signed off successfully";
                    showWarningToast(msg);
                    handleClose();
                    callbackFunc && callbackFunc();
                    return;
                  }
                  handleClose();
                  //   const {
                  //     data: { message },
                  //   } = res;
                  //   showSuccessToast(message);
                  //   handleClose();
                  //   callbackFunc && callbackFunc();
                })
                .catch((error) => {
                  handleAPICatch(error);
                })
                .finally(() => {
                  setSubmitting(false);
                });
            }}
            validate={(values) => {
              const errors = {};
              if (!values.startTime) {
                errors.startTime = "Shift Start Time is Required";
              }
              if (!values.endTime) {
                errors.endTime = "Shift End Time is Required";
              }
              return errors;
            }}
          >
            {({
              submitForm,
              isSubmitting,
              errors,
              touched,
              isValid,
              setFieldValue,
              values,
              setValues,
            }) => <SignOffForm handleClose={handleClose} />}
          </Formik>
        </div>
      </div>
    </ModalPopUp>
  );
};

export default SignOffModal;
